// dynamic-form-table.component.ts
import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { FormArray, FormBuilder, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { CustomFormControlComponent } from '@shared/components/editor/custom-form-control/custom-form-control.component';
import { MatTable } from '@angular/material/table';
import { options } from 'sanitize-html';
import { ChipEditor } from '@shared/components/chip-editor/chip-editor.component';
import { DialogService } from '@shared/components/dialog/dialog.service';
import {
  ConfirmDialogComponent,
  ConfirmDialogConfig,
} from '@shared/components/dialog/confirm-dialog/confirm-dialog.component';

export type ColumnConfig =
  | {
      key: string;
      type: 'string' | 'list';
      label: string;
      options?: never;
      required?: boolean;
    }
  | {
      key: string;
      type: 'hidden';
      label?: never;
      options?: never;
      required?: boolean;
    }
  | {
      key: string;
      type: 'selection-list';
      label: string;
      options: { id: string; value: string }[];
      required?: boolean;
    };

@Component({
  selector: 'recrewt-dynamic-form-table',
  templateUrl: './dynamic-form-table.component.html',
  styleUrls: ['./dynamic-form-table.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DynamicFormTableComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class DynamicFormTableComponent extends CustomFormControlComponent implements OnInit {
  private fb = inject(FormBuilder);
  private dialog = inject<DialogService<any, any>>(DialogService);

  @Input() config!: ColumnConfig[];

  @Input() showSave = false;

  @Output() save = new EventEmitter<void>();

  @ViewChild(MatTable) table?: MatTable<any>;

  formArray: FormArray = this.fb.array([]);

  isDisabled = false;

  displayedColumns: string[] = [];

  protected readonly options = options;

  ngOnInit() {
    this.displayedColumns = [
      ...this.config.filter((it) => it.type !== 'hidden').map((c) => c.key),
      'actions',
    ];
  }

  writeValue(value: any[]): void {
    this.formArray.clear();

    if (value && Array.isArray(value)) {
      value.forEach((item) => {
        const group = this.fb.group({});
        this.config.forEach((col) => {
          group.addControl(col.key, this.fb.control(item[col.key]));
        });
        this.formArray.push(group);
        this.table?.renderRows();
      });
    }

    this.formArray.valueChanges.subscribe((val) => this.onChange(val));
  }

  override setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (isDisabled) {
      this.formArray.disable();
    } else {
      this.formArray.enable();
    }
  }

  addRow() {
    const group = this.fb.group({});
    this.config.forEach((col) => {
      group.addControl(
        col.key,
        this.fb.control(
          col.type === 'selection-list' || col.type === 'list' ? [] : undefined,
          this.getValidator(col),
        ),
      );
    });
    this.formArray.push(group);
    this.onChange(this.formArray.value);
    this.table?.renderRows();
  }

  getValidator(col: ColumnConfig) {
    if (!col.required) {
      return null;
    }
    if (col.type === 'selection-list' || col.type === 'list') {
      return ChipEditor.required();
    } else {
      return Validators.required;
    }
  }

  removeRow(index: number) {
    this.dialog.openWithCallback({
      type: ConfirmDialogComponent,
      dialogData: {
        title: 'Element löschen',
        message: 'Möchten Sie dieses Element wirklich löschen?',
        confirmText: 'Löschen',
        cancelText: 'Abbrechen',
        confirmColor: 'warn',
      } as ConfirmDialogConfig,
      confirmCallback: () => {
        this.formArray.removeAt(index);
        this.onChange(this.formArray.value);
        this.table?.renderRows();
      },
    });
  }
}
